import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { submitToWeb3Forms, WEB3FORMS_ESTIMATE_ACCESS_KEY } from '../lib/web3forms';

type Question = {
  prompt: string;
  type: 'single' | 'text' | 'contact';
  options?: string[];
  placeholder?: string;
};

const QUESTIONS: Question[] = [
  {
    prompt: 'What type of property do you have?',
    type: 'single',
    options: [
      'Apartment',
      'Condo/Townhouse',
      'House (under 1,500 sq ft)',
      'House (1,500-2,500 sq ft)',
      'House (2,500-3,500 sq ft)',
      'House (over 3,500 sq ft)',
    ],
  },
  {
    prompt: 'How many bedrooms does your space have?',
    type: 'single',
    options: ['Studio', '1 Bedroom', '2 Bedrooms', '3 Bedrooms', '4 Bedrooms', '5+ Bedrooms'],
  },
  {
    prompt: 'How many bathrooms does your space have?',
    type: 'single',
    options: ['1', '1.5', '2', '2.5', '3', '3+'],
  },
  {
    prompt: 'What type of cleaning do you need?',
    type: 'single',
    options: ['Standard Cleaning', 'Deep Cleaning', 'Move-In / Move-Out', 'Post-Construction', 'Disinfection Service'],
  },
  {
    prompt: 'How often would you like cleaning?',
    type: 'single',
    options: ['One-Time', 'Weekly', 'Bi-Weekly', 'Monthly'],
  },
  {
    prompt: 'Do you have any pets in the home?',
    type: 'single',
    options: ['No Pets', 'Dog(s)', 'Cat(s)', 'Other Pets'],
  },
  {
    prompt: 'Are there any areas that need special attention?',
    type: 'single',
    options: ['Kitchen', 'Bathrooms', 'Windows', 'Carpets & Rugs', 'None in particular'],
  },
  {
    prompt: 'When would you like your first cleaning?',
    type: 'single',
    options: ['As Soon As Possible', 'Within a Week', 'Within a Month', 'Just Researching'],
  },
  {
    prompt: 'What day of the week works best for you?',
    type: 'single',
    options: ['Weekdays', 'Weekends', 'Either Works'],
  },
  {
    prompt: 'What time of day do you prefer?',
    type: 'single',
    options: ['Morning', 'Afternoon', 'Evening', 'No Preference'],
  },
  {
    prompt: "What's your name, address, and best contact number?",
    type: 'contact',
  },
  {
    prompt: "Lastly, what's your email address?",
    type: 'text',
    placeholder: 'your@email.com',
  },
];

export default function EstimateQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactAddress, setContactAddress] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const total = QUESTIONS.length;
  const question = QUESTIONS[step];
  const percent = Math.round(((step + 1) / total) * 100);

  const goNext = async () => {
    if (step < total - 1) {
      setStep(step + 1);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);

    let email = '';
    const answerPairs: string[] = [];
    QUESTIONS.forEach((q, i) => {
      if (q.type === 'contact' || !answers[i]) return;
      if (q.type === 'text') {
        email = answers[i];
      } else {
        answerPairs.push(`${q.prompt} ${answers[i]}`);
      }
    });

    const fields: Record<string, string> = {
      Name: contactName,
      Phone: contactPhone,
      Address: contactAddress,
      Email: email,
      Message: answerPairs.join(', '),
    };

    const ok = await submitToWeb3Forms(
      `New Free Estimate Request - ${contactName || 'Website Visitor'}`,
      fields,
      WEB3FORMS_ESTIMATE_ACCESS_KEY
    );

    setIsSubmitting(false);
    if (ok) {
      setSubmitted(true);
    } else {
      setSubmitError(true);
    }
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const selectOption = (option: string) => {
    setAnswers((prev) => ({ ...prev, [step]: option }));
    setTimeout(goNext, 250);
  };

  if (submitted) {
    return (
      <div className="bg-sand-50 rounded-3xl p-8 lg:p-10 border border-sand-100 flex flex-col items-center justify-center text-center min-h-[420px]">
        <div className="w-16 h-16 rounded-full clean-gradient flex items-center justify-center mb-6 shadow-lg shadow-axis-500/20">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-display text-2xl font-semibold text-axis-900 mb-3">Thanks! We've got your details.</h3>
        <p className="text-gray-600 max-w-sm">
          Our team will review your answers and reach out shortly with your free estimate. You can also reach us
          directly at 240-750-9535.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-sand-50 rounded-3xl p-8 lg:p-10 border border-sand-100">
      {/* Progress */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <span>
          Question {step + 1} of {total}
        </span>
        <span>{percent}% Complete</span>
      </div>
      <div className="w-full h-1.5 bg-sand-200 rounded-full overflow-hidden mb-8">
        <motion.div
          className="h-full clean-gradient rounded-full"
          initial={false}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <h3 className="font-display text-xl sm:text-2xl font-semibold text-axis-950 mb-6">{question.prompt}</h3>

          {question.type === 'single' && (
            <div className="space-y-3 mb-2">
              {question.options!.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => selectOption(option)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-200 ${
                    answers[step] === option
                      ? 'bg-axis-500 border-axis-500 text-white'
                      : 'bg-white border-sand-200 text-gray-700 hover:border-axis-300 hover:bg-axis-50/50'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {question.type === 'text' && (
            <div className="mb-2">
              <input
                type="email"
                value={answers[step] || ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [step]: e.target.value }))}
                placeholder={question.placeholder}
                className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
              />
            </div>
          )}

          {question.type === 'contact' && (
            <div className="grid sm:grid-cols-2 gap-4 mb-2">
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
              />
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="(240) 000-0000"
                className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
              />
              <input
                type="text"
                value={contactAddress}
                onChange={(e) => setContactAddress(e.target.value)}
                placeholder="Street address, city, state"
                className="w-full px-4 py-3 bg-white border border-sand-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all sm:col-span-2"
              />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-sand-200">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 0}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed enabled:text-gray-600 enabled:hover:text-axis-700 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        {/* Dots */}
        <div className="hidden sm:flex items-center gap-1.5">
          {QUESTIONS.map((_, i) => (
            <span
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step ? 'w-5 h-1.5 bg-axis-500' : 'w-1.5 h-1.5 bg-sand-300'
              }`}
            />
          ))}
        </div>

        {question.type === 'single' ? (
          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-axis-700 transition-colors"
          >
            Skip
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={goNext}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 clean-gradient text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md shadow-axis-500/20 hover:shadow-axis-500/40 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                {step === total - 1 ? 'Get My Free Estimate' : 'Continue'}
                {step === total - 1 ? <Send className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </>
            )}
          </button>
        )}
      </div>

      {submitError && (
        <p className="text-sm text-red-500 mt-4 text-center">
          Something went wrong sending your answers. Please try again or call us at 240-750-9535.
        </p>
      )}
    </div>
  );
}
